/**
 * La navigation de l'administration : ses quatre parties, et le menu des
 * deux qui vivent sous `/admin` (Comptabilité, Planification).
 *
 * ## Ce qui n'existe pas encore est visible, et inactif
 *
 * La cible du cadrage (26/09/2026) est une arborescence complète. Beaucoup
 * d'entrées n'ont pas encore de backend : une entrée qui mènerait à une page
 * vide, ou à des totaux inventés, mentirait. Elles figurent donc au menu,
 * **sans lien**, avec la raison — la cartographie du Lot UI 0 (§ 6 et § 7)
 * donne chacune.
 *
 * `Utilisateurs` (Planification › Administration) est le module
 * `/admin/users`, à deux tuiles ; `Comptes clients / familles` (Comptabilité ›
 * Clients) mène à sa tuile financière. Il n'est pas dupliqué.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Ban, BarChart4, BookOpen, BookOpenCheck, Building2, Calendar, CalendarCheck, ClipboardList, Clock,
  FileText, Gift, Hourglass, Landmark, LayoutDashboard, LayoutGrid, List, ListOrdered, Mail, PlusCircle, Receipt,
  School, ScrollText, Settings, Tag, UserCheck, Users, Wallet,
} from 'lucide-react';
import { CHEMINS } from './chemins';

export interface EntreeActive {
  libelle: string;
  chemin: string;
  icone: LucideIcon;
  /**
   * Un raccourci vers une page rangée dans une autre partie : il y mène, mais
   * ne la revendique pas (ni menu allumé, ni fil d'Ariane).
   */
  ailleurs?: string;
}

export interface EntreeInactive {
  libelle: string;
  /** Pourquoi l'entrée ne mène nulle part encore. */
  raison: string;
}

export type Entree = EntreeActive | EntreeInactive;

export const estActive = (e: Entree): e is EntreeActive => 'chemin' in e;

export interface Rubrique {
  titre: string;
  entrees: readonly Entree[];
}

export interface Espace {
  cle: 'comptabilite' | 'planification';
  titre: string;
  accueil: string;
  rubriques: readonly Rubrique[];
}

const C = CHEMINS.comptabilite;
const P = CHEMINS.planification;
const U = CHEMINS.utilisateurs;

export const COMPTABILITE: Espace = {
  cle: 'comptabilite',
  titre: 'Comptabilité',
  accueil: C.accueil,
  rubriques: [
    {
      titre: 'Comptabilité',
      entrees: [{ libelle: 'Tableau de bord', chemin: C.accueil, icone: LayoutDashboard }],
    },
    {
      titre: 'Banque & caisse',
      entrees: [
        { libelle: 'Rapprochement et imports', chemin: C.rapprochement, icone: Landmark },
        { libelle: 'Compte d’attente (499000)', chemin: C.attente, icone: Hourglass },
        { libelle: 'Stripe', raison: 'Aucun fait source Stripe (soldes et frais) : à décider.' },
        { libelle: 'Caisse', raison: 'Aucun fait source de caisse encore.' },
      ],
    },
    {
      titre: 'Clients',
      entrees: [
        { libelle: 'Factures et notes de crédit', chemin: C.factures, icone: Receipt },
        { libelle: 'Comptes clients / familles', chemin: U.finances, icone: Wallet, ailleurs: 'Planification › Utilisateurs' },
        { libelle: 'Attestations fiscales', chemin: C.attestations, icone: ScrollText },
      ],
    },
    {
      titre: 'Fournisseurs',
      entrees: [
        { libelle: 'Factures fournisseurs', raison: 'Aucun modèle de facture fournisseur : Lot UI 4.' },
      ],
    },
    {
      titre: 'Écritures',
      entrees: [
        { libelle: 'Journaux', chemin: C.journaux, icone: BookOpen },
        { libelle: 'Toutes les écritures', chemin: C.ecritures, icone: ListOrdered },
        { libelle: 'Opérations diverses', raison: 'Le geste de saisie d’OD reste à écrire : Lot UI 2.' },
      ],
    },
    {
      titre: 'Analytique',
      entrees: [{ libelle: 'Ventilations', raison: 'Écran à construire : Lot UI 2.' }],
    },
    {
      titre: 'Rapports',
      entrees: [
        { libelle: 'Contrôles et tableau financier', chemin: C.controles, icone: BarChart4 },
        { libelle: 'Balance et grand livre', raison: 'Lot UI 3.' },
        { libelle: 'Bilan et compte de résultats', raison: 'Schéma des comptes annuels à fournir (à valider par la comptable).' },
      ],
    },
    {
      titre: 'Clôture',
      entrees: [
        { libelle: 'Reprise d’ouverture 2026', chemin: C.reprise, icone: BookOpenCheck },
        { libelle: 'Périodes et clôture', raison: 'Lot UI 5.' },
      ],
    },
    {
      titre: 'Configuration',
      entrees: [
        { libelle: 'Plan comptable, journaux, axes', raison: 'Tables en place, écrans au Lot UI 2.' },
      ],
    },
  ],
};

export const PLANIFICATION: Espace = {
  cle: 'planification',
  titre: 'Planification',
  accueil: P.accueil,
  rubriques: [
    {
      /*
        Les lieux servent les stages ET les journées pédagogiques : un stage ne
        se programme que dans un centre de vacances, une journée pédagogique
        partout (VALIDÉ ÉCLOSION, 20/10/2026).
      */
      titre: 'Lieux',
      entrees: [
        { libelle: 'Centres', chemin: P.centres, icone: Building2 },
        { libelle: 'Nouveau centre', chemin: P.centreNouveau, icone: PlusCircle },
        { libelle: 'Écoles', chemin: P.ecoles, icone: School },
      ],
    },
    {
      titre: 'Vacances',
      entrees: [
        { libelle: 'Stages', chemin: P.stages, icone: Calendar },
        { libelle: 'Programmation', chemin: P.programmation, icone: Clock },
        { libelle: 'Inscriptions admin', chemin: P.inscriptions, icone: ClipboardList },
        { libelle: 'Inscriptions par groupe', chemin: P.groupes, icone: UserCheck },
        { libelle: 'Liste d’attente', chemin: P.listeAttente, icone: List },
      ],
    },
    {
      titre: 'Journées pédagogiques',
      entrees: [
        { libelle: 'Journées et inscrits', chemin: P.jpa, icone: School },
        { libelle: 'Programmer une journée', chemin: P.jpaProgrammer, icone: CalendarCheck },
      ],
    },
    {
      /*
        La garderie par abonnement des écoles (ISA, CSSA) : le brouillon de
        facturation du mois ; les liens de pointage sont dans l'application
        de gestion (Présences du centre), les familles et abonnés viennent de
        l'import QuickSchool (docs/garderie/CADRAGE.md).
      */
      titre: 'Garderie',
      entrees: [
        { libelle: 'Facturation mensuelle', chemin: P.garderieFacturation, icone: Receipt },
        { libelle: 'Familles et abonnés', raison: 'Écran à construire : familles, enfants et abonnements viennent de l’import QuickSchool.' },
      ],
    },
    {
      titre: 'Parascolaire',
      entrees: [
        { libelle: 'Activités', raison: 'Pas encore d’écran : l’ancienne page ne disait que « à venir ».' },
        { libelle: 'Programmation', raison: 'Pas encore d’écran : l’ancienne page ne disait que « à venir ».' },
      ],
    },
    {
      titre: 'Demandes',
      entrees: [
        { libelle: 'Annulations', chemin: P.annulations, icone: Ban },
        { libelle: 'Inclusion', chemin: P.inclusion, icone: FileText },
      ],
    },
    {
      titre: 'Administration',
      entrees: [
        { libelle: 'Utilisateurs', chemin: U.accueil, icone: Users },
        { libelle: 'Bons cadeaux', chemin: P.bonsCadeaux, icone: Gift },
        { libelle: 'Conditions tarifaires', chemin: P.tarifs, icone: Tag },
        { libelle: 'Newsletter', chemin: P.newsletter, icone: Mail },
        { libelle: 'Communication', raison: 'Pas encore d’écran : l’ancienne page ne disait que « à venir ».' },
        { libelle: 'Paramètres du site', chemin: P.parametresSite, icone: Settings },
      ],
    },
  ],
};

export const ESPACES: readonly Espace[] = [COMPTABILITE, PLANIFICATION];

export interface Partie {
  libelle: string;
  chemin: string;
  icone: LucideIcon;
  /** Allumée seulement sur son adresse exacte (le tableau de bord). */
  exact: boolean;
  detail: string;
}

/**
 * Les quatre parties de l'administration, dans l'ordre de la barre (décidé
 * par Éclosion le 27/09/2026). Gestion est l'espace `/staff`, qui garde sa
 * propre application ; on y passe, on n'y est pas « dans l'admin ».
 */
export const PARTIES: readonly Partie[] = [
  {
    libelle: 'Tableau de bord', chemin: CHEMINS.tableauDeBord, icone: LayoutGrid, exact: true,
    detail: 'Les informations générales, et les raccourcis vers les pages qui les traitent.',
  },
  {
    libelle: 'Comptabilité', chemin: C.accueil, icone: BarChart4, exact: false,
    detail: 'Banque et compte d’attente, clients, ventes, journaux et écritures, contrôles, reprise d’ouverture.',
  },
  {
    libelle: 'Planification', chemin: P.accueil, icone: ClipboardList, exact: false,
    detail: 'Centres, stages, programmation, parascolaire, tarifs, demandes, utilisateurs.',
  },
  {
    libelle: 'Gestion', chemin: '/staff', icone: CalendarCheck, exact: false,
    detail: 'Le quotidien des centres : groupes, présences, enfants, communication.',
  },
];

/**
 * L'entrée qui porte la page courante, pour le fil d'Ariane et le menu.
 * La plus longue adresse qui préfixe le chemin l'emporte : la fiche
 * `/admin/users/…/finances` n'allume pas « Utilisateurs » si une entrée plus
 * précise existe.
 */
export function entreeCourante(
  espace: Espace,
  pathname: string,
): { rubrique: Rubrique; entree: EntreeActive } | null {
  let meilleure: { rubrique: Rubrique; entree: EntreeActive } | null = null;
  for (const rubrique of espace.rubriques) {
    for (const entree of rubrique.entrees) {
      if (!estActive(entree) || entree.ailleurs) continue;
      const exacte = pathname === entree.chemin;
      const prefixe = entree.chemin !== espace.accueil && pathname.startsWith(`${entree.chemin}/`);
      if ((exacte || prefixe) && (!meilleure || entree.chemin.length > meilleure.entree.chemin.length)) {
        meilleure = { rubrique, entree };
      }
    }
  }
  return meilleure;
}

/**
 * La partie à laquelle une page appartient, pour allumer la barre. Une page
 * rangée dans le menu d'un espace sans vivre sous son préfixe (le module
 * `/admin/users`, rangé dans Planification) appartient à cet espace.
 */
export function partieCourante(pathname: string): Partie | null {
  const sous = (chemin: string) => pathname === chemin || pathname.startsWith(`${chemin}/`);
  for (const partie of PARTIES) {
    if (partie.exact ? pathname === partie.chemin : sous(partie.chemin)) return partie;
  }
  for (const espace of ESPACES) {
    if (entreeCourante(espace, pathname)) return PARTIES.find((p) => p.chemin === espace.accueil) ?? null;
  }
  return null;
}
